import { useEffect, useState } from 'react';

import { createIconLoader } from './IconLoader';

export type IconFallback = {
  collection: string;
  icon: string;
};

const iconLoader = createIconLoader();

export function useIconImg(
  collection: string,
  icon: string,
  fallback?: IconFallback,
): string | undefined {
  const [iconImg, setIconImg] = useState<string | undefined>();
  const [hasError, setHasError] = useState(false);

  const finalCollection =
    hasError && fallback ? fallback.collection : collection;
  const finalIcon = hasError && fallback ? fallback.icon : icon;

  const cacheState = iconLoader.load(finalCollection, finalIcon);

  useEffect(() => {
    if (cacheState.state === 'loaded') {
      setIconImg(cacheState.icon);

      return undefined;
    }

    let shouldUpdate = true;

    async function loadIcon(): Promise<void> {
      if (cacheState.state === 'loading') {
        try {
          const rawImg = await cacheState.promise;

          if (shouldUpdate) {
            setIconImg(rawImg);
          }
        } catch (e) {
          setHasError(true);
        }
      }
    }

    loadIcon().catch((e) => {
      if (shouldUpdate) {
        setIconImg(undefined);
      }
      console.error(e);
    });

    return () => {
      shouldUpdate = false;
    };
  }, [icon, collection, iconImg, cacheState]);

  return cacheState.state === 'loaded' ? cacheState.icon : iconImg;
}
